/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component } from '@angular/core';
import { type Params, ActivatedRoute, Router } from '@angular/router';
import { type JobResponse, EventService, TimeService, UtilService } from '@dm/core';
import { MO } from 'app/common/constants';
import { type Initiator, type Lun, type Mapping } from 'app/common/types/mo';
import { MAX_LUN_NUMBER } from 'app/feature/service/block-service/lun-group/luns.const';
import { cloneDeep, isEqual } from 'lodash';
import { type Subscription } from 'rxjs';

import { type GuideConfig, type GuideStepItem } from '../../smart-assistant';
import { GUIDE_POLLING_TIME, GuideCreateOptions, GuideEventKey, GuideStorageKey, HOST_DISPLAY_NUM, MAX_DISPLAY_COUNT } from '../../smart-assistant.const';
import { SmartAssistantService } from '../../smart-assistant.service';
import { SmartAssistantStrService } from '../../smart-assistant-str.service';

@Component({
    selector: 'app-storage-resources-guidance-steps',
    templateUrl: './guidance-steps.component.html',
    styleUrls: ['./guidance-steps.component.scss'],
})
export class GuidanceStepsComponent implements OnInit, OnDestroy {
    i18n = this.str.i18n;
    MAX_DISPLAY_COUNT = MAX_DISPLAY_COUNT;
    GuideCreateOptions = GuideCreateOptions;
    /**
     * 步骤数据
     */
    stepItems: GuideStepItem[];
    /**
     * 指导所在步骤
     */
    activeStep = GuideCreateOptions.CreateLun;
    /**
     * 用户当前所在步骤
     */
    currentStep: GuideCreateOptions;
    /**
     * 订阅用户当前所在步骤
     */
    watchStep$: Subscription;
    /**
     * 引导开始的按钮
     */
    showStartBtn = true;

    /**
     * “创建LUN”动作的订阅
     */
    lunSub$: Subscription;
    /**
     * LUN信息的更新轮询
     */
    lunPolling$: NodeJS.Timeout;

    /**
     * 创建主机的订阅
     */
    hostSub$: Subscription;
    /**
     * 主机数量最多1个
     */
    HOST_DISPLAY_NUM = HOST_DISPLAY_NUM;
    /**
     * 在引导中创建的主机id
     */
    hostId: string;
    /**
     * 主机信息的更新轮询
     */
    hostPolling$: NodeJS.Timeout;

    /**
     * “创建启动器”动作的订阅
     */
    initiatorSub$: Subscription;
    /**
     * 启动器信息的更新轮询
     */
    initiatorPolling$: NodeJS.Timeout;

    /**
     * “映射”动作的订阅
     */
    mapSub$: Subscription;
    /**
     * 映射信息的更新轮询
     */
    mapPolling$: NodeJS.Timeout;

    /**
     * 步骤对应的数据更新函数
     */
    updateMap: Map<GuideCreateOptions, (isPreQuery?: boolean) => void>;
    /**
     * 各步骤订阅函数
     */
    subscribeMap: Map<GuideCreateOptions, () => void>;

    /**
     * 是否点击了“继续创建”等按钮
     */
    isCreateMore = false;

    /**
     * 记录上次更新时，Guide的信息
     */
    lastGuideInfo = this.service.getGuideAllConfig();
    /**
     * 判断是否首次进入指导页（用于触发接口查询）
     */
    isFirstEnter = true;

    constructor(
        public str: SmartAssistantStrService,
        private service: SmartAssistantService,
        private router: Router,
        private route: ActivatedRoute,
        private eventService: EventService,
        private utilService: UtilService,
        private timeService: TimeService,
    ) {
        this.updateMap = new Map([
            [GuideCreateOptions.CreateLun, this.getLunInfo],
            [GuideCreateOptions.CreateHost, this.getHostInfo],
            [GuideCreateOptions.AddInitiator, this.getInitiatorInfo],
            [GuideCreateOptions.MapLunToHost, this.getMapInfo],
        ]);

        this.subscribeMap = new Map([
            [GuideCreateOptions.CreateLun, this.lunSubscribe],
            [GuideCreateOptions.CreateHost, this.hostSubscribe],
            [GuideCreateOptions.AddInitiator, this.initiatorSubscribe],
            [GuideCreateOptions.MapLunToHost, this.mapSubscribe],
        ]);
    }

    ngOnInit(): void {
        this.initSteps();

        // 监听用户当前所在步骤，更新步骤边框和颜色
        if (!this.watchStep$) {
            this.watchStep$ = this.eventService.subscribe(GuideEventKey.assistantConfigChange,
                (params: GuideConfig) => {
                    if (isEqual(this.lastGuideInfo, params)) {
                        return;
                    }

                    this.lastGuideInfo = params;
                    this.getStepStyle();
                    if (this.service.checkStepValid(params.stepForCreateMore)) {
                        this.currentStep = params.stepForCreateMore;
                    }
                },
            );
        }
    }

    /**
     * 初始化步骤
     */
    initSteps(): void {
        // 里面有数组，要做深拷贝
        this.stepItems = cloneDeep(this.str.stepItems);
        this.getStepStyle();

        // 如果有未完成的步骤，则继续
        const lastStep = this.service.getGuideConfig(GuideStorageKey.latestSteps);
        if (this.service.checkStepValid(lastStep)) {
            this.showStartBtn = false;
            this.updateInfo();
        }
        this.isFirstEnter = false;
    }

    /**
     * 从“创建LUN”开始引导
     */
    startGuiding(): void {
        this.showStartBtn = false;
        this.service.setGuideConfig(GuideStorageKey.startGuiding, true, false);
        this.updateStep(GuideCreateOptions.CreateLun);

        // 更新引导开始时间
        this.service.setGuideConfig(GuideStorageKey.startTime, this.timeService.formatUTC(this.timeService.currTime * 1e3));

        this.jumpToPage();
        this.setSubscribe();
    }

    getStepStyle(): void {
        this.stepItems.forEach((stepItem, i) => {
            this.getIconStyle(stepItem);
            this.checkBtnShow(i, stepItem);
            this.getOperateStyle(i, stepItem);
        });
    }

    /**
     * 获取步骤icon的样式
     */
    getIconStyle(stepItem: GuideStepItem): void {
        stepItem.stepFinished = Boolean(stepItem.info && !stepItem.objDeletedTip);
    }

    /**
     * 获取步骤信息框的样式
     */
    getOperateStyle(step: GuideCreateOptions, stepItem: GuideStepItem): void {
        const style = {
            'background-color': step <= this.activeStep || stepItem.stepFinished ? '#ffffff' : '#f2f2f2',
            border: '',
            'font-weight': step <= this.activeStep ? '700' : '400',
            color: step <= this.activeStep || stepItem.stepFinished ? '#1a1a1a' : '#4d4d4d',
            'align-items': stepItem.info ? 'normal' : 'center',
        };

        // 引导中的当前步骤，才显示边框
        const borderShow = step === this.currentStep &&
            this.service.getGuideConfig(GuideStorageKey.startGuiding) &&
            !this.service.getGuideConfig(GuideStorageKey.showPrimaryBtn);
        if (borderShow) {
            style.border = '1px solid #2673e5';
        }

        // 当步骤显示primary按钮时（未开始引导时，也要显示），边框为灰色
        const notStart = step === GuideCreateOptions.CreateLun && this.activeStep === GuideCreateOptions.CreateLun && this.showStartBtn;
        if (notStart || stepItem.showInitialBtn) {
            style.border = '1px solid #e6e6e6';
        }

        stepItem.style = style;
    }

    /**
     * 检查步骤Primary和more按钮的显示
     */
    checkBtnShow(step: GuideCreateOptions, stepItem: GuideStepItem): void {
        const stepNoObj = Boolean((step === this.activeStep && !stepItem.info) || stepItem.objDeletedTip);
        if (step > GuideCreateOptions.CreateHost && !this.hostId) {
            stepItem.showInitialBtn = false;
            return;
        }

        stepItem.showInitialBtn = stepNoObj &&
            (step !== this.currentStep ||
                !this.service.getGuideConfig(GuideStorageKey.startGuiding) ||
                this.service.getGuideConfig(GuideStorageKey.showPrimaryBtn));
        if (step === GuideCreateOptions.CreateLun && stepItem.showInitialBtn) {
            // 初始化时，创建LUN步骤只显示“开始”按钮
            stepItem.showInitialBtn = !this.showStartBtn;
        }

        // 引导中最多创建100个LUN
        if (step === GuideCreateOptions.CreateLun) {
            const createdLunNum = this.service.getGuideConfig(GuideStorageKey.createdLuns)?.length ?? 0;
            stepItem.hideMorebtn = createdLunNum === MAX_LUN_NUMBER.fromLungroup;
        }
    }

    /**
     * 点击步骤primary按钮后进行的操作
     */
    doStepGuide(step: GuideCreateOptions): void {
        this.clearParams();
        this.service.setGuideConfig(GuideStorageKey.startGuiding, true, false);
        this.service.setGuideConfig(GuideStorageKey.showPrimaryBtn, false, false);
        this.service.setGuideConfig(GuideStorageKey.stepForCreateMore, null);
        this.updateStep(step);
        this.jumpToPage();
        this.setSubscribe();
    }

    /**
     * 更新已创建信息
     * @param step 不传step则是从“未完成的进程”进入，需要更新所有步骤的信息
     * @param isPolling 是否是轮询触发，轮询不需要执行页面跳转
     */
    updateInfo(step?: GuideCreateOptions, isPolling?: boolean): void {
        const guideConfig = this.service.getGuideAllConfig();

        this.activeStep = guideConfig.step ?? GuideCreateOptions.CreateLun;
        this.currentStep = guideConfig.stepForCreateMore ?? this.activeStep;

        // 传入了步骤，只执行该步骤的更新
        if (this.service.checkStepValid(step)) {
            this.updateMap.get(step)();
        } else {
            // 未传入步骤，则当前步骤之前的信息都需要更新
            let queryStep = this.activeStep;

            // 保证获取到的是用户实际执行到的步骤
            if (this.service.checkStepValid(guideConfig.latestSteps) &&
                guideConfig.latestSteps > this.activeStep &&
                this.isFirstEnter
            ) {
                queryStep = guideConfig.latestSteps;
            }

            for (let i = 0; i < queryStep; i++) {
                this.updateMap.get(i)();
            }
        }

        if (!isPolling) {
            this.jumpToPage();
        }
    }

    /**
     * 获取已创建LUN的信息
     */
    getLunInfo = (isPreQuery?: boolean): void => {
        const lunList = this.service.getGuideConfig(GuideStorageKey.createdLuns);
        const stepLun = this.stepItems[GuideCreateOptions.CreateLun];
        if (lunList && lunList.length) {
            // 最多显示3条
            const handledLun = [];
            lunList.slice(0, 3).forEach((lun: Lun) => {
                const lunCap = this.utilService.adaptUnit(lun.CAPACITY, 'Sector').label;
                handledLun.push(`${lun.NAME}, ${lunCap}`);
            });

            // 组装已创建LUN的描述
            stepLun.objList = handledLun;
            stepLun.objCount = lunList.length;
            stepLun.info = this.str.createdObjDes.get(GuideCreateOptions.CreateLun)(lunList.length);
            stepLun.objDeletedTip = '';
            stepLun.showInitialBtn = false;

            // 轮询LUN的信息
            this.setLunPolling();
        } else {
            // 当SessionStorage中查不到lun的信息时，可能是lun被删除了
            stepLun.info = '';

            // 当前步骤不是创建LUN，则需要显示提示
            if (!isPreQuery && this.activeStep !== GuideCreateOptions.CreateLun) {
                stepLun.objDeletedTip = this.i18n.objDeletedInfo;
                stepLun.showInitialBtn = true;

                if (this.lunPolling$) {
                    clearInterval(this.lunPolling$);
                    this.lunPolling$ = null;
                }

                // 如果当前正在创建主机，但是又未创出来，需要跳转到创建LUN步骤
                if (this.activeStep === GuideCreateOptions.CreateHost && !this.hostId) {
                    this.updateStep(GuideCreateOptions.CreateLun);
                    this.jumpToPage(GuideCreateOptions.CreateLun);
                }
            }

            if (!isPreQuery && !stepLun.objDeletedTip) {
                this.updateStep(GuideCreateOptions.CreateLun);
            }

            this.getStepStyle();
        }
        this.getStepStyle();
    };

    /**
     * 获取已创建主机的信息
     */
    getHostInfo = (isPreQuery?: boolean): void => {
        const host = this.service.getGuideConfig(GuideStorageKey.createdHost);
        const stepHost = this.stepItems[GuideCreateOptions.CreateHost];
        if (host && Object.keys(host).length) {
            this.hostId = host.ID;

            // 只引导用户创建1个主机
            stepHost.objCount = this.HOST_DISPLAY_NUM;
            stepHost.info = this.str.createdObjDes.get(GuideCreateOptions.CreateHost)(this.HOST_DISPLAY_NUM);
            stepHost.objDeletedTip = '';
            stepHost.showInitialBtn = false;
            this.service.setGuideConfig(GuideStorageKey.isHostDeleted, false, false);

            // 轮询主机的信息
            this.setHostPolling();
        } else {
            // 当SessionStorage中查不到主机的信息时，可能是主机被删除了
            stepHost.info = '';
            this.hostId = '';

            // 当前步骤不是创建主机，则需要显示提示
            if (!isPreQuery && this.activeStep !== GuideCreateOptions.CreateHost) {
                stepHost.objDeletedTip = this.i18n.objDeletedInfo;
                stepHost.showInitialBtn = true;

                // 主机被删除后，需要将启动器和映射信息清空
                this.service.setInitiatorlist([], true);
                this.service.setMaplist([], true);
                this.service.setGuideConfig(GuideStorageKey.isHostDeleted, true);

                if (this.hostPolling$) {
                    clearInterval(this.hostPolling$);
                    this.hostPolling$ = null;
                }

                if (this.initiatorPolling$) {
                    clearInterval(this.initiatorPolling$);
                    this.initiatorPolling$ = null;
                }

                if (this.mapPolling$) {
                    clearInterval(this.mapPolling$);
                    this.mapPolling$ = null;
                }
            }

            if (!isPreQuery) {
                this.updateStep(GuideCreateOptions.CreateHost);
            }

            this.getStepStyle();
        }
        stepHost.btnLabel = stepHost.objDeletedTip ? this.i18n.createMoreBtn : this.i18n.modifyHostBtn;
        this.getStepStyle();
    };

    /**
     * 获取已添加启动器的信息
     */
    getInitiatorInfo = (isPreQuery?: boolean): void => {
        const guideConfig = this.service.getGuideAllConfig();
        const initiatorList = guideConfig.createdInitiator;
        const stepInitiator = this.stepItems[GuideCreateOptions.AddInitiator];
        if (initiatorList && initiatorList.length) {
            stepInitiator.info = this.handleInitiatorDes(initiatorList);
            stepInitiator.objDeletedTip = '';
            stepInitiator.showInitialBtn = false;

            // 轮询启动器的信息
            this.setInitiatorPolling();
        } else {
            // 当SessionStorage中查不到启动器信息时，可能是启动器被删除了
            stepInitiator.info = '';

            // 当前步骤不是添加启动器并且主机没被删除，则需要显示提示
            if (!isPreQuery && this.activeStep !== GuideCreateOptions.AddInitiator) {
                stepInitiator.objDeletedTip = this.hostId ? this.i18n.objDeletedInfo : '';
                stepInitiator.showInitialBtn = this.hostId ? true : false;

                if (this.initiatorPolling$) {
                    clearInterval(this.initiatorPolling$);
                    this.initiatorPolling$ = null;
                }

                // 如果当前正在映射LUN，但是又未完成映射，需要跳转到添加启动器的步骤
                if (this.activeStep === GuideCreateOptions.MapLunToHost && guideConfig.mappedLun.length === 0) {
                    this.updateStep(GuideCreateOptions.AddInitiator);
                }
            }

            if (!isPreQuery && !stepInitiator.objDeletedTip && this.hostId) {
                this.updateStep(GuideCreateOptions.AddInitiator);
            }
            this.getStepStyle();
        }
        this.getStepStyle();
    };

    /**
     * 处理已添加启动器的描述
     */
    handleInitiatorDes(initiatorList: Initiator[]): string {
        const fcList = [];
        const iscsiList = [];
        const nvmeList = [];
        initiatorList.forEach((initiator: Initiator) => {
            if (initiator.TYPE === MO.FC_TYPE) {
                fcList.push(initiator);
                return;
            }

            if (initiator.TYPE === MO.ISCSI_TYPE) {
                iscsiList.push(initiator);
                return;
            }

            if (initiator.TYPE === MO.NVMeoF_RoCE_TYPE) {
                nvmeList.push(initiator);
                return;
            }
        });

        let description = '';

        // FC启动器
        if (fcList.length) {
            description += this.str.createdObjDes.get(GuideCreateOptions.AddInitiator)(fcList.length, MO.FC_TYPE) + '\n';
        }

        // ISCSI启动器
        if (iscsiList.length) {
            description += this.str.createdObjDes.get(GuideCreateOptions.AddInitiator)(iscsiList.length, MO.ISCSI_TYPE) + '\n';
        }

        // NVME over RoCE启动器
        if (nvmeList.length) {
            description += this.str.createdObjDes.get(GuideCreateOptions.AddInitiator)(nvmeList.length, MO.NVMeoF_RoCE_TYPE) + '\n';
        }

        return description;
    }

    /**
     * 获取已映射的信息
     */
    getMapInfo = (isPreQuery?: boolean): void => {
        const mapList = this.service.getGuideConfig(GuideStorageKey.mappedLun);
        const stepMapped = this.stepItems[GuideCreateOptions.MapLunToHost];
        if (mapList && mapList.length) {
            // 组装已映射LUN的描述
            stepMapped.objCount = mapList.length;
            stepMapped.info = this.str.createdObjDes.get(GuideCreateOptions.MapLunToHost)(mapList.length);
            stepMapped.objDeletedTip = '';
            stepMapped.showInitialBtn = false;

            // 轮询映射的信息
            this.setMapPolling();
        } else {
            // 当SessionStorage中查不到映射信息时，可能是映射关系被删除了
            stepMapped.info = '';

            // 当前步骤不是映射LUN，则需要显示提示
            if (!isPreQuery && this.activeStep !== GuideCreateOptions.MapLunToHost) {
                stepMapped.objDeletedTip = this.hostId ? this.i18n.objDeletedInfo : '';
                stepMapped.showInitialBtn = this.hostId ? true : false;

                if (this.mapPolling$) {
                    clearInterval(this.mapPolling$);
                    this.mapPolling$ = null;
                }
            }

            if (!isPreQuery && !stepMapped.objDeletedTip && this.hostId) {
                this.updateStep(GuideCreateOptions.MapLunToHost);
            }
            this.getStepStyle();
        }
        this.getStepStyle();
    };

    /**
     * 更新步骤
     * @param step 步骤
     * @param modifyCurrentStep 是否需要将当前步骤修改为指导步骤
     */
    updateStep(step: GuideCreateOptions, modifyCurrentStep = true): void {
        this.activeStep = step;
        this.service.setGuideConfig(GuideStorageKey.step, step);

        if (modifyCurrentStep) {
            this.currentStep = this.service.getGuideConfig(GuideStorageKey.stepForCreateMore) ?? this.activeStep;
        }
        this.getStepStyle();
    }

    /**
     * 设置LUN轮询，定时检查LUN是否被修改或删除
     */
    setLunPolling(): void {
        if (this.lunPolling$) {
            return;
        }

        if (this.isFirstEnter) {
            this.service.lunPollingFn();
        }

        this.lunPolling$ = setInterval(this.service.lunPollingFn, GUIDE_POLLING_TIME);
    }

    /**
     * 设置主机轮询，定时检查主机是否被修改或删除
     */
    setHostPolling(): void {
        if (this.hostPolling$) {
            return;
        }

        if (this.isFirstEnter) {
            this.service.hostPollingFn();
        }

        this.hostPolling$ = setInterval(this.service.hostPollingFn, GUIDE_POLLING_TIME);
    }

    /**
     * 设置启动器轮询，定时检查启动器是否被修改或删除
     */
    setInitiatorPolling(): void {
        if (this.initiatorPolling$) {
            return;
        }

        if (this.isFirstEnter) {
            this.initiatorPollingFn();
        }

        this.initiatorPolling$ = setInterval(this.initiatorPollingFn, GUIDE_POLLING_TIME);
    }

    initiatorPollingFn = (): void => {
        const initiatorList = this.service.getGuideConfig(GuideStorageKey.createdInitiator);
        const host = this.service.getGuideConfig(GuideStorageKey.createdHost);
        if (!initiatorList || !initiatorList.length || !host || !Object.keys(host).length) {
            return;
        }

        this.service.getHostInitiators(host.ID, { doException: false }).subscribe(
            (data: Initiator[]) => {
                this.service.setInitiatorlist(data, true);
            },
            (res: JobResponse) => {
                this.service.setInitiatorlist(res.data ?? [], true);
            },
        );
    };

    /**
     * 设置映射轮询，定时检查映射关系是否被修改或删除
     */
    setMapPolling(): void {
        if (this.mapPolling$) {
            return;
        }

        if (this.isFirstEnter) {
            this.mapPollingFn();
        }

        this.mapPolling$ = setInterval(this.mapPollingFn, GUIDE_POLLING_TIME);
    }

    mapPollingFn = (): void => {
        const host = this.service.getGuideConfig(GuideStorageKey.createdHost);
        if (!host || !Object.keys(host).length) {
            return;
        }

        this.service.getHostMapingLunByRest(host.ID, { doException: false }).subscribe(
            (data: Mapping[]) => {
                this.service.setMaplist(data, true);
            },
            (res: JobResponse) => {
                this.service.setMaplist(res.data ?? [], true);
            },
        );
    };

    /**
     * 跳转到对应页面进行操作
     * @param step 通过“继续创建”跳转才会传入step
     */
    jumpToPage(step?: GuideCreateOptions): void {
        // 如果有step，说明是通过“继续创建”进入的
        let jumpStep = this.activeStep;
        if (this.service.checkStepValid(step)) {
            jumpStep = step;
            this.service.setGuideConfig(GuideStorageKey.startGuiding, true, false);
            this.service.setGuideConfig(GuideStorageKey.showPrimaryBtn, false, false);
            this.service.setGuideConfig(GuideStorageKey.stepForCreateMore, step);
        }

        this.isCreateMore = this.service.checkStepValid(this.service.getGuideConfig(GuideStorageKey.stepForCreateMore));
        this.getStepStyle();
        jumpStep = this.preQuery(jumpStep);

        // 当前步骤之前的步骤都需要设置订阅
        for (let i = 0; i <= jumpStep; i++) {
            this.setSubscribe(i);
        }

        if (this.service.checkStepValid(jumpStep, true)) {
            const guideStepItem = this.stepItems[jumpStep];
            guideStepItem.showInitialBtn = false;
            setTimeout(() => {
                this.router.navigate([guideStepItem.url], {
                    queryParams: this.getRouterParams(guideStepItem),
                });
            }, 500);
        }
    }

    /**
     * 预查询当前步骤是否有数据，有的话则跳过该步骤
     */
    preQuery(step: GuideCreateOptions): GuideCreateOptions {
        if (this.isCreateMore || this.activeStep === GuideCreateOptions.Finished) {
            return step;
        }

        let doCheck = true;
        let nextStep = step;
        while (doCheck) {
            if (nextStep === GuideCreateOptions.Finished) {
                doCheck = false;
                break;
            }
            this.updateMap.get(nextStep)(true);

            // 如果下一步能查到数据，则说明已经创建过对象，跳过该步骤
            const stepInfo = this.stepItems[nextStep].info;
            if (stepInfo && !this.isCreateMore) {
                nextStep++;
            } else {
                doCheck = false;
            }
        }

        this.updateStep(nextStep);
        return nextStep;
    }

    /**
     * 获取router的queryParams参数进行路由跳转
     * @param stepItem 要进行的步骤
     * @returns 路由 queryParams 参数
     */
    getRouterParams(stepItem: GuideStepItem): Params {
        const queryParams: Params = {
            tab: stepItem.tab,
            ...this.hostId ?
                {
                    action: stepItem.action,
                    actionParams: this.hostId,
                } :
                {},
        };

        // LUN不需要打开详情，因此不需要该参数
        if (stepItem.objType === GuideCreateOptions.CreateLun) {
            delete queryParams.actionParams;
        }

        // 点击“修改主机信息”需要打开主机修改页面
        if (stepItem.objType === GuideCreateOptions.CreateHost &&
            this.service.getGuideConfig(GuideStorageKey.stepForCreateMore) === GuideCreateOptions.CreateHost
        ) {
            queryParams.action = 'searchModify';
        }

        // 首次创建主机或者主机被删除后重新创建主机时，不要加action和actionParams（避免打开一个Title为空的详情页）
        const needDeleteAction = (stepItem.objType === GuideCreateOptions.CreateHost && this.activeStep === GuideCreateOptions.CreateHost) ||
            (stepItem.objType === GuideCreateOptions.CreateHost && stepItem.btnLabel === this.i18n.createMoreBtn);
        if (needDeleteAction) {
            delete queryParams.action;
            delete queryParams.actionParams;
        }

        return queryParams;
    }

    /**
     * 设置对象创建结果的订阅
     * @param step 通过“继续创建”跳转才会传入step
     */
    setSubscribe(step?: GuideCreateOptions): void {
        if (step === GuideCreateOptions.Finished) {
            return;
        }

        let subStep = this.activeStep;
        if (this.service.checkStepValid(step)) {
            subStep = step;
        }
        this.subscribeMap.get(subStep)();
    }

    /**
     * 监听通过引导创建的LUN
     */
    lunSubscribe = (): void => {
        if (!this.lunSub$) {
            this.lunSub$ = this.eventService.subscribe(GuideEventKey.lunCreated, (isPolling) => {
                this.afterCreate(GuideCreateOptions.CreateLun, GuideCreateOptions.CreateHost, isPolling);
            });
        }
    };

    /**
     * 监听通过引导创建的主机
     */
    hostSubscribe = (): void => {
        if (!this.hostSub$) {
            this.hostSub$ = this.eventService.subscribe(GuideEventKey.hostCreated, (isPolling) => {
                this.afterCreate(GuideCreateOptions.CreateHost, GuideCreateOptions.AddInitiator, isPolling);
            });
        }
    };

    /**
     * 监听通过引导添加的启动器
     */
    initiatorSubscribe = (): void => {
        if (!this.initiatorSub$) {
            this.initiatorSub$ = this.eventService.subscribe(GuideEventKey.initiatorCreated, (isPolling) => {
                this.afterCreate(GuideCreateOptions.AddInitiator, GuideCreateOptions.MapLunToHost, isPolling);
            });
        }
    };

    /**
     * 监听通过引导映射的LUN
     */
    mapSubscribe = (): void => {
        if (!this.mapSub$) {
            this.mapSub$ = this.eventService.subscribe(GuideEventKey.mappedLun, (isPolling) => {
                this.afterCreate(GuideCreateOptions.MapLunToHost, GuideCreateOptions.Finished, isPolling);
            });
        }
    };

    /**
     * 根据引导创建对象后，需要更新信息
     * @param currentStep 回调函数所属的步骤
     * @param nextStep 将步骤更新到nextStep
     */
    async afterCreate(currentStep: GuideCreateOptions, oriNextStep: GuideCreateOptions, isPolling?: boolean): Promise<any> {
        let nextStep = oriNextStep;
        this.isCreateMore = this.service.checkStepValid(this.service.getGuideConfig(GuideStorageKey.stepForCreateMore));

        if (!isPolling) {
            this.service.setGuideConfig(GuideStorageKey.showPrimaryBtn, false, false);

            // 提前检查LUN是否都被映射
            if (nextStep === GuideCreateOptions.MapLunToHost) {
                const mapDatas = await this.service.getHostMapingLunByRest(this.hostId, { doException: false }).toPromise();
                if (mapDatas?.length) {
                    const mapLunIds = [];
                    mapDatas.forEach((mapData: Mapping) => {
                        if (mapData.lunId) {
                            mapLunIds.push(mapData.lunId);
                        }
                    });

                    const allMapped = this.service.getGuideConfig(GuideStorageKey.createdLuns)
                        .every((lun: Lun) => mapLunIds.includes(lun.ID));

                    // 如果通过引导创建的LUN都完成映射，则提前更新映射信息并将步骤设置为完成，否则还是要进行第4步的引导
                    if (allMapped) {
                        nextStep = GuideCreateOptions.Finished;
                        this.service.setMaplist(mapDatas);
                        this.getMapInfo();
                    }
                }
            }

            // “轮询”和“创建更多”都不更新步骤
            if (!this.isCreateMore) {
                this.service.setGuideConfig(GuideStorageKey.step, nextStep);
            } else {
                // 重置stepForCreateMore标识，stepForCreateMore不需要更新步骤
                this.service.setGuideConfig(GuideStorageKey.stepForCreateMore, null);
            }
        }

        this.updateInfo(currentStep, isPolling);
    }

    /**
     * 清空路由参数
     */
    clearParams(): void {
        const action = this.route.snapshot.queryParams.action;
        const tab = this.route.snapshot.queryParams.tab;
        if (action || tab) {
            this.router.navigate([], {
                queryParams: null,
            });
        }
    }

    ngOnDestroy(): void {
        this.watchStep$?.unsubscribe();

        this.lunSub$?.unsubscribe();
        if (this.lunPolling$) {
            clearInterval(this.lunPolling$);
        }

        this.hostSub$?.unsubscribe();
        if (this.hostPolling$) {
            clearInterval(this.hostPolling$);
        }

        this.initiatorSub$?.unsubscribe();
        if (this.initiatorPolling$) {
            clearInterval(this.initiatorPolling$);
        }

        this.mapSub$?.unsubscribe();
        if (this.mapPolling$) {
            clearInterval(this.mapPolling$);
        }

        this.clearParams();
    }
}
